import React from 'react';
import { AlertCircle } from 'lucide-react';

/**
 * TextArea Component
 * 
 * Multi-line text input with label, helper text, and error states.
 * 
 * @example
 * ```tsx
 * <TextArea
 *   label="Description"
 *   placeholder="Enter details..."
 *   rows={4}
 * />
 * ```
 */

export interface TextAreaProps extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'size'> {
  /** Label text */
  label?: string;
  /** Helper text */
  helperText?: string;
  /** Error message */
  error?: string;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Full width */
  fullWidth?: boolean;
  /** Container className */
  containerClassName?: string;
  /** Show character count */
  showCount?: boolean;
  /** Maximum length */
  maxLength?: number;
}

const TextArea = React.forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      label,
      helperText,
      error,
      size = 'md',
      fullWidth = false,
      containerClassName = '',
      className = '',
      id,
      disabled,
      required,
      showCount = false,
      maxLength,
      value,
      ...props
    },
    ref
  ) => {
    const textAreaId = id || `textarea-${React.useId()}`;
    
    // Character count
    const currentLength = typeof value === 'string' ? value.length : 0;

    // Base styles
    const baseStyles = [
      'bg-white border rounded-lg',
      'text-gray-900 placeholder-gray-400',
      'transition-all duration-200',
      'focus:outline-none focus:ring-2',
      'disabled:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60',
      'resize-vertical',
      'dark:bg-dark-card dark:text-dark-text',
      'dark:placeholder-gray-500 dark:disabled:bg-dark-surface',
    ].join(' ');

    // State styles
    const stateStyles = error
      ? 'border-error-500 focus:border-error-500 focus:ring-error-500/20'
      : 'border-gray-200 focus:border-primary-500 focus:ring-primary-500/20 dark:border-dark-border';

    // Size styles
    const sizeStyles = {
      sm: 'px-3 py-1.5 text-sm',
      md: 'px-3 py-2.5 text-base',
      lg: 'px-4 py-3 text-lg',
    };

    const widthStyles = fullWidth ? 'w-full' : '';
    const containerWidth = fullWidth ? 'w-full' : '';

    const textAreaStyles = [
      baseStyles,
      stateStyles,
      sizeStyles[size],
      widthStyles,
      className,
    ].join(' ');

    return (
      <div className={`${containerWidth} ${containerClassName}`}>
        {/* Label */}
        {label && (
          <label
            htmlFor={textAreaId}
            className="block text-sm font-medium text-gray-900 mb-1.5 dark:text-dark-text"
          >
            {label}
            {required && <span className="text-error-500 ml-1">*</span>}
          </label>
        )}

        {/* TextArea */}
        <textarea
          ref={ref}
          id={textAreaId}
          disabled={disabled}
          required={required}
          maxLength={maxLength}
          value={value}
          className={textAreaStyles}
          aria-invalid={error ? 'true' : 'false'}
          aria-describedby={
            error
              ? `${textAreaId}-error`
              : helperText
              ? `${textAreaId}-helper`
              : undefined
          }
          {...props}
        />

        {/* Character Count */}
        {showCount && maxLength && (
          <div className="mt-1 text-right">
            <span
              className={`text-xs ${
                currentLength > maxLength
                  ? 'text-error-600 dark:text-error-400'
                  : 'text-gray-500 dark:text-gray-400'
              }`}
            >
              {currentLength} / {maxLength}
            </span>
          </div>
        )}

        {/* Helper Text or Error */}
        {(helperText || error) && (
          <p
            id={error ? `${textAreaId}-error` : `${textAreaId}-helper`}
            className={`mt-1.5 text-sm ${
              error
                ? 'text-error-600 dark:text-error-400'
                : 'text-gray-500 dark:text-gray-400'
            }`}
          >
            {error || helperText}
          </p>
        )}
      </div>
    );
  }
);

TextArea.displayName = 'TextArea';

export default TextArea;
